import { Box, Flex, Text } from '@chakra-ui/react';
import { FileText, Link2, ListChecks, MessageCircle, Plus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { navActionAtom } from '../store';
import { useT } from '../i18n';

const TABS = [
  { label: 'account.nav.chats', path: '/', icon: MessageCircle },
  { label: 'account.nav.documents', path: '/documents', icon: FileText },
  { label: 'account.nav.links', path: '/links', icon: Link2 },
  { label: 'account.nav.lists', path: '/lists', icon: ListChecks },
] as const;

interface AppNavProps {
  /** Floating at the bottom (phones) or inline in the header (desktop). */
  placement: 'floating' | 'inline';
}

// The family apps as one dark pill (Living Room): the current app shows its
// name on saffron, the others are icons, and "+" is the page's main action
// (new chat, upload, new list) when it has one.
export function AppNav({ placement }: AppNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const action = useAtomValue(navActionAtom);
  const t = useT();

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <Flex
      as="nav"
      aria-label={t('account.nav.label')}
      gap={1}
      p="6px"
      bg="nav.bg"
      borderRadius="full"
      boxShadow={placement === 'floating' ? '0 12px 24px -8px rgba(31, 29, 27, 0.4)' : 'none'}
      {...(placement === 'floating'
        ? {
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'max(env(safe-area-inset-bottom), 16px)',
            zIndex: 2,
          }
        : {})}
    >
      {TABS.map((tab) => {
        const active = isActive(tab.path);
        return (
          <Box
            key={tab.path}
            as="a"
            href={tab.path}
            aria-current={active ? 'page' : undefined}
            aria-label={active ? undefined : t(tab.label)}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              navigate(tab.path);
            }}
            h="48px"
            minW="48px"
            px={active ? 4 : 0}
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="6px"
            bg={active ? 'saffron' : 'transparent'}
            color={active ? '#1F1D1B' : 'nav.fg'}
            fontWeight={700}
            fontSize="15px"
            textDecoration="none"
            _hover={{ bg: active ? 'saffron' : 'whiteAlpha.100' }}
            _focusVisible={{ outline: '2px solid', outlineColor: 'saffron', outlineOffset: '2px' }}
          >
            <tab.icon size={active ? 20 : 22} aria-hidden="true" />
            {active && <Text as="span">{t(tab.label)}</Text>}
          </Box>
        );
      })}
      {action && (
        <Box
          as="button"
          type="button"
          aria-label={action.label}
          onClick={action.onSelect}
          w="48px"
          h="48px"
          borderRadius="full"
          bg="accent.solid"
          color="accent.onSolid"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'brand.600' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'saffron', outlineOffset: '2px' }}
        >
          <Plus size={22} aria-hidden="true" />
        </Box>
      )}
    </Flex>
  );
}
